import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import i18n from "../../i18n";
import { Controller } from "react-hook-form";
import { Button } from "../button";
import TextInput from "../inputs/TextInput";
import { useGetFRDefaultGroups } from "../../hooks/common/query";
import {
  useSyncFRDevicesGroupScheduleByLocation,
  useUpdateFRDefaultGroups,
} from "../../hooks/common/mutation";
import toast from "react-hot-toast";
import Loader from "../loader";

export const GroupsSchedulesFRConfigurationsCardSchema = Yup.object({
  accessGroupSchedule: Yup.string().required(
    i18n.language === "en"
      ? "Access group/schedule is required."
      : "مطلوب مجموعة الوصول/الجدول."
  ),
  noAccessGroupSchedule: Yup.string().required(
    i18n.language === "en"
      ? "No access group/schedule is required."
      : "لا يلزم وجود مجموعة أو جدول وصول."
  ),
});

interface GroupsSchedulesFRConfigurationsCardProps {
  selectedDeviceIPAddresses: string[];
}
interface FormData {
  accessGroupSchedule: string;
  noAccessGroupSchedule: string;
}

const GroupsSchedulesFRConfigurationsCard = ({
  selectedDeviceIPAddresses,
}: GroupsSchedulesFRConfigurationsCardProps) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useFormWithSchema(GroupsSchedulesFRConfigurationsCardSchema);

  const { data: frDefaultGroups, isPending: isfrDefaultGroupsLoading } =
    useGetFRDefaultGroups();

  const {
    mutateAsync: UpdateFRDefaultGroups,
    isPending: isUpdateFRDefaultGroupsLoading,
  } = useUpdateFRDefaultGroups();

  const {
    mutateAsync: SyncFRDevicesGroupScheduleByLocation,
    isPending: isSyncFRDevicesGroupScheduleByLocationLoading,
  } = useSyncFRDevicesGroupScheduleByLocation();

  useEffect(() => {
    if (frDefaultGroups) {
      reset({
        accessGroupSchedule: frDefaultGroups.accessGroupSchedule,
        noAccessGroupSchedule: frDefaultGroups.noAccessGroupSchedule,
      });
    }
  }, [frDefaultGroups, reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body = {
          accessGroupSchedule: data.accessGroupSchedule,
          noAccessGroupSchedule: data.noAccessGroupSchedule,
        };
        await UpdateFRDefaultGroups(body);

        toast.success(t("groupsSchedulesConfigurationUpdatedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }

      if (selectedDeviceIPAddresses.length === 0) {
        toast.error(t("kindlySelectDeviceToSync"));
      } else {
        try {
          const body = {
            accessGroupSchedule: data.accessGroupSchedule,
            noAccessGroupSchedule: data.noAccessGroupSchedule,
            fripAddresses: selectedDeviceIPAddresses,
          };

          await SyncFRDevicesGroupScheduleByLocation(body);
          toast.success(t("groupsSchedulesConfigurationSyncedSuccessfully"));
        } catch (error) {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      SyncFRDevicesGroupScheduleByLocation,
      selectedDeviceIPAddresses,
      UpdateFRDefaultGroups,
      t,
    ]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-lg lg:text-xl font-medium">
        {t("groupsSchedulesConfiguration")}
      </h2>

      {isfrDefaultGroupsLoading ? (
        <Loader />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid grid-cols-3 gap-2 md:gap-4 items-end"
        >
          <Controller
            name="accessGroupSchedule"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("accessGroupSchedule")}
                onChange={onChange}
                value={value || ""}
                error={errors?.[name]?.message}
              />
            )}
          />

          <Controller
            name="noAccessGroupSchedule"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("noAccessGroupSchedule")}
                onChange={onChange}
                value={value || ""}
                error={errors?.[name]?.message}
              />
            )}
          />

          <Button
            isLoading={
              isUpdateFRDefaultGroupsLoading ||
              isSyncFRDevicesGroupScheduleByLocationLoading
            }
            className="max-w-max !py-[14px] !px-9"
          >
            {t("pushSync")}
          </Button>
        </form>
      )}
    </div>
  );
};

export default React.memo(GroupsSchedulesFRConfigurationsCard);
